import { Box, Flex, Heading, Icon, SimpleGrid, Text, useColorModeValue } from '@chakra-ui/react'
import { Activity, AlertCircle, DollarSign, TrendingDown, TrendingUp } from 'lucide-react'
import React from 'react'

interface KPICardProps {
  title: string
  value: string
  subtitle?: string
  icon: React.ElementType
  trend?: 'up' | 'down' | 'neutral'
  trendValue?: string
  variant?: 'default' | 'success' | 'warning' | 'destructive'
}

export const KPICard = ({ title, value, subtitle, icon, trend, trendValue, variant = 'default' }: KPICardProps) => {
  const bgBlue = useColorModeValue('blue.50', 'blue.900')
  const bgGreen = useColorModeValue('green.50', 'green.900')
  const bgOrange = useColorModeValue('orange.50', 'orange.900')
  const bgRed = useColorModeValue('red.50', 'red.900')
  const cardBg = useColorModeValue('gray.700', 'gray.700')

  const variantClasses = {
    default: { bg: bgBlue, color: 'blue.500' },
    success: { bg: bgGreen, color: 'green.500' },
    warning: { bg: bgOrange, color: 'orange.500' },
    destructive: { bg: bgRed, color: 'red.500' },
  }

  const trendClasses = {
    up: 'green.500',
    down: 'red.500',
    neutral: 'white',
  }

  return (
    <Box
      borderWidth="1px"
      borderRadius="lg"
      p={6}
      _hover={{ boxShadow: 'lg' }}
      transition="box-shadow 0.2s"
      bg={cardBg}>
      <Flex alignItems="center" gap={2}>
        <Flex
          p={3}
          borderRadius="md"
          bg={variantClasses[variant].bg}
          color={variantClasses[variant].color}
          flexShrink={0}>
          <Icon as={icon} w={4} h={4} />
        </Flex>
        <Box>
          <Text fontSize="sm" fontWeight="medium" color="white">
            {title}
          </Text>
          <Heading as="h3" size="md">
            {value}
          </Heading>
        </Box>
      </Flex>
      <Box mt={4}>
        {subtitle && (
          <Text fontSize="sm" color="white">
            {subtitle}
          </Text>
        )}
        {trend && trendValue && (
          <Text fontSize="sm" fontWeight="medium" color={trendClasses[trend]} mt={1}>
            {trend === 'up' ? '↑' : trend === 'down' ? '↓' : '→'} {trendValue}
          </Text>
        )}
      </Box>
    </Box>
  )
}

interface CardsContainerProps {
  data: any
  isPositiveGrowth: (growth?: string) => boolean
}

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value)

export const CardsContainer: React.FC<CardsContainerProps> = ({ data, isPositiveGrowth }) => {
  return (
    <Box bg="gray.800">
      <SimpleGrid columns={{ base: 1, md: 2, lg: 5 }} spacing={6} mb={8}>
        <KPICard
          title="Receita Total"
          value={formatCurrency(data.currentRevenue)}
          subtitle="Mês Atual"
          icon={DollarSign}
          trend={data.revenueGrowth ? (isPositiveGrowth(data.revenueGrowth) ? 'up' : 'down') : 'neutral'}
          trendValue={`${data.revenueGrowth ?? '0%'} vs mês anterior`}
          variant="success"
        />

        <KPICard
          title="Despesas Totais"
          value={formatCurrency(data.currentExpenses)}
          subtitle="Mês Atual"
          icon={TrendingDown}
          trend={data.expensesGrowth ? (isPositiveGrowth(data.expensesGrowth) ? 'up' : 'down') : 'neutral'}
          trendValue={`${data.expensesGrowth ?? '0%'} vs mês anterior`}
          variant="warning"
        />

        <KPICard
          title="Lucro Líquido"
          value={formatCurrency(data.netProfit)}
          subtitle="Receitas - Despesas"
          icon={TrendingUp}
          trend={data.profitGrowth ? (isPositiveGrowth(data.profitGrowth) ? 'up' : 'down') : 'neutral'}
          trendValue={`${data.profitGrowth ?? '0%'} vs mês anterior`}
          variant="success"
        />

        <KPICard
          title="Contas em Aberto"
          value={formatCurrency(data.overdueAccounts)}
          subtitle={`${data.overdueCount} ${
            data.overdueCount === 1 ? 'cliente inadimplente' : 'clientes inadimplentes'
          }`}
          icon={AlertCircle}
          trend="neutral"
          trendValue={`${data.avgDaysOverdue} dias média`}
          variant="destructive"
        />

        <KPICard
          title="Fluxo de Caixa"
          value={formatCurrency(data.projectedCashFlow)}
          subtitle="Projeção Q3"
          icon={Activity}
          trend={data.projectionGrowth ? (isPositiveGrowth(data.projectionGrowth) ? 'up' : 'down') : 'neutral'}
          trendValue={`${data.projectionGrowth ?? '0%'} projetado`}
          variant="default"
        />
      </SimpleGrid>
    </Box>
  )
}

export default CardsContainer
